<template>
  <div>
    <div class="floating-phone-button" :class="{ disabled: serviceDisabled || userAccountDisabled }"
      @click="openPhoneDial">
      <div class="phone-icon-container">
        <CIcon name="cil-phone" class="phone-icon" />
        
        <!-- Call Counter Badge -->
        <div v-if="ringingCallsCount > 0" class="call-badge" @click.stop="showIncomingAlerts">
          <span class="badge-number">{{ ringingCallsCount }}</span>
        </div>
        
        <span class="tooltip">{{ serviceDisabled ? 'VoIP Service Disabled'
          : userAccountDisabled ? 'VoIP Account Unavailable'
            : ringingCallsCount > 0 ? `${ringingCallsCount} incoming call${ringingCallsCount > 1 ? 's' : ''}`
              : 'Phone Dial' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'FloatingPhoneButton',
  computed: {
    ...mapGetters(['voipEnabled', 'voipAccountStatus', 'voipRingingCalls']),

    // VoIP Service Status
    serviceDisabled: {
      get() { return this.voipEnabled === false; },
      set(value) { this.$store.commit('setVoipEnabled', !value); }
    },

    userExtension() { return this.authUser?.user?.voip_extension; },
    userAccountDisabled: {
      get() { return [null, 'Unavailable'].includes(this.voipAccountStatus) },
      set(value) { this.$store.commit('setVoipAccountStatus', value || null); }
    },

    // Incoming calls count
    ringingCallsCount() {
      return this.voipRingingCalls.length || 0;
    },
  },
  methods: {
    openPhoneDial() {
      if (this.serviceDisabled || this.userAccountDisabled) return;
      this.$store.commit('openPhoneDialModal', null);
    },

    showIncomingAlerts() {
      if (this.ringingCallsCount <= 0) return;
      try {
        window.dispatchEvent(new CustomEvent('voip-show-incoming-alerts'));
      } catch (e) {
        // no-op
      }
    }
  }
}
</script>

<style scoped>
.floating-phone-button {
  position: fixed;
  right: 16px;
  bottom: 40px;
  z-index: 1000;
  cursor: pointer;
  transition: all 0.3s ease;
}

.floating-phone-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.phone-icon-container {
  position: relative;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  overflow: visible;
}

.phone-icon-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.phone-icon-container:hover::before {
  opacity: 1;
}

.phone-icon {
  font-size: 2rem !important;
  width: 2rem !important;
  height: 2rem !important;
  color: white;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

.phone-icon-container:hover .phone-icon {
  transform: scale(1.1);
}

/* Call Badge */
.call-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  border: 2px solid white;
  border-radius: 50%;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-badge 1.5s infinite;
  z-index: 3;
}

.badge-number {
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
  }
}

.tooltip {
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 1001;
}

.tooltip::after {
  content: '';
  position: absolute;
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  border-left: 5px solid rgba(0, 0, 0, 0.8);
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
}

.phone-icon-container:hover .tooltip {
  opacity: 1;
  visibility: visible;
  right: 75px;
}

.floating-phone-button:hover {
  transform: translateY(-2px);
}

.floating-phone-button:hover .phone-icon-container {
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.4);
  transform: scale(1.05);
}

/* Pulse animation */
@keyframes pulse {
  0% {
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  }

  50% {
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.6);
  }

  100% {
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  }
}

.phone-icon-container {
  animation: pulse 2s infinite;
}

.phone-icon-container:hover {
  animation: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .floating-phone-button {
    right: 15px;
    bottom: 40px;
  }

  .phone-icon-container {
    width: 50px;
    height: 50px;
  }

  .phone-icon {
    font-size: 1.5rem !important;
    width: 1.5rem !important;
    height: 1.5rem !important;
  }

  .call-badge {
    min-width: 20px;
    height: 20px;
    top: -6px;
    right: -6px;
  }

  .badge-number {
    font-size: 0.7rem;
  }

  .tooltip {
    display: none;
  }
}

@media (max-width: 480px) {
  .floating-phone-button {
    right: 10px;
    bottom: 30px;
  }

  .phone-icon-container {
    width: 45px;
    height: 45px;
  }

  .phone-icon {
    font-size: 1.3rem !important;
    width: 1.3rem !important;
    height: 1.3rem !important;
  }

  .call-badge {
    min-width: 18px;
    height: 18px;
    top: -5px;
    right: -5px;
  }

  .badge-number {
    font-size: 0.65rem;
  }
}
</style>
